import React, { useMemo } from 'react';
import { Paper, Stack, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faRefresh } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../common/SearchableSelect';
import { useTranslation } from 'react-i18next';

const ChallanFilter = ({
    supervisors = [],
    supervisorsLoading = false,
    selectedSupervisorId,
    onSupervisorChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    status,
    onStatusChange,
    groupBy,
    onGroupByChange,
    challanPrinted,
    onChallanPrintedChange,
    unitType,
    onUnitTypeChange,
    onSubmit,
    loading,
    activeTab,
    disableSupervisorSelector = false,
}) => {
    const { t } = useTranslation();
    const supervisorOptions = useMemo(() =>
        supervisors.map((s) => ({
            value: s.id,
            label: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || s.email || String(s.id),
        })),
        [supervisors]
    );

    const statusOptions = [
        { value: '', label: t('common.analyticsColumns.allStatuses') },
        { value: 'pending', label: t('common.analyticsColumns.pending') },
        { value: 'loaded', label: t('common.analyticsColumns.loaded') },
        { value: 'in_transit', label: t('common.analyticsColumns.inTransit') },
        { value: 'delivered', label: t('common.analyticsColumns.delivered') },
        { value: 'cancelled', label: t('common.analyticsColumns.cancelled') },
    ];

    const showGroupBy = activeTab === 'products';
    const showStatus = activeTab !== 'summary' && activeTab !== 'daily';

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3, mb: 3, borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.08)',
                bgcolor: '#FFFFFF',
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>

                {!disableSupervisorSelector && (
                    <div style={{ minWidth: 220 }}>
                        <SearchableSelect
                            options={supervisorOptions}
                            value={selectedSupervisorId}
                            onChange={(e) => onSupervisorChange(e.target.value)}
                            placeholder={t('common.analyticsColumns.allSupervisors')}
                            disabled={supervisorsLoading}
                            loading={supervisorsLoading}
                        />
                    </div>
                )}

                <TextField
                    label={t('common.analyticsColumns.fromDate')}
                    type="date"
                    value={fromDate}
                    onChange={(e) => onFromDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />
                <TextField
                    label={t('common.analyticsColumns.toDate')}
                    type="date"
                    value={toDate}
                    onChange={(e) => onToDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />

                {showStatus && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{t('common.analyticsColumns.status')}</InputLabel>
                        <Select value={status} label={t('common.analyticsColumns.status')} onChange={(e) => onStatusChange(e.target.value)} sx={selectSx}>
                            {statusOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {showGroupBy && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{t('common.analyticsColumns.groupBy')}</InputLabel>
                        <Select value={groupBy} label={t('common.analyticsColumns.groupBy')} onChange={(e) => onGroupByChange(e.target.value)} sx={selectSx}>
                            <MenuItem value="parent">{t('common.analyticsColumns.productCatalogue')}</MenuItem>
                            <MenuItem value="sku">{t('common.analyticsColumns.productSizeSku')}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('common.analyticsColumns.challanPrinted')}</InputLabel>
                    <Select value={challanPrinted} label={t('common.analyticsColumns.challanPrinted')} onChange={(e) => onChallanPrintedChange(e.target.value)} sx={selectSx}>
                        <MenuItem value="">{t('common.analyticsColumns.all')}</MenuItem>
                        <MenuItem value="true">{t('common.analyticsColumns.printed')}</MenuItem>
                        <MenuItem value="false">{t('common.analyticsColumns.notPrinted')}</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>{t('common.analyticsColumns.unitType')}</InputLabel>
                    <Select value={unitType} label={t('common.analyticsColumns.unitType')} onChange={(e) => onUnitTypeChange(e.target.value)} sx={selectSx}>
                        <MenuItem value="">{t('common.analyticsColumns.all')}</MenuItem>
                        <MenuItem value="kg">{t('common.analyticsColumns.kg')}</MenuItem>
                        <MenuItem value="ton">{t('common.analyticsColumns.ton')}</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={loading || !fromDate || !toDate}
                    startIcon={<FontAwesomeIcon icon={loading ? faRefresh : faSearch} className={loading ? 'animate-spin' : ''} />}
                    sx={{
                        minWidth: 120, height: 40, borderRadius: 2,
                        textTransform: 'none', fontWeight: 600,
                        bgcolor: '#189AB4', boxShadow: 'none',
                        '&:hover': { bgcolor: '#05445E', boxShadow: '0 4px 12px rgba(5,68,94,0.3)' },
                    }}
                >
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </Stack>
        </Paper>
    );
};

const selectSx = {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#75E6DA' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#189AB4' },
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        '&:hover fieldset': { borderColor: '#75E6DA' },
        '&.Mui-focused fieldset': { borderColor: '#189AB4' },
    },
};

export default ChallanFilter;
